import { type Company, type CompanyTag, type Currency, type FundingRound, type Headcount } from "@prisma/client";
import { map } from "bluebird";
import { type AppContext } from "~/lib/context";
import { chain } from "~/lib/lodash";
import { type FetchBenchmarkLevelsRange } from "~/services/benchmark-level/fetchBenchmarkLevelsRange";
import { filterEmployeesInRange } from "~/services/benchmark-level/filterEmployeesInRange";
import { hasLevel } from "~/services/employee";
import { type EmployeeLevel } from "~/services/employee/employeeLevel";
import { type WeightedJob } from "~/services/job";
import { type MarketDataEmployee } from "~/services/market-data/getMarketDataEmployees";
import { getEmployeeLocationsForPredictiveBenchmark } from "~/services/market-data/getMarketDataPredictiveBenchmark";
import { computeMarketCompensationStats } from "~/services/marketCompensationStats";
import { type PercentileBounds } from "~/services/percentiles";

export type ComputeMarketDataByLevelOptions = {
  levels: EmployeeLevel[];
  benchmarkLevelsRange: FetchBenchmarkLevelsRange;
  useBenchmarkLevelsInShare: boolean;
  locationIds: number[];
  allowedLevels: EmployeeLevel[];
  employees: MarketDataEmployee[];
  currency: Currency;
  weightedJobs?: WeightedJob[];
  percentiles: PercentileBounds;
  isForCompany?: boolean;
  company?: Company;
  headcount?: Headcount | null;
  industry?: CompanyTag | null;
  fundingRounds?: FundingRound[];
  mergeAdvancedLevels?: boolean;
};

export const computeMarketDataByLevel = async (ctx: AppContext, options: ComputeMarketDataByLevelOptions) => {
  const {
    levels,
    benchmarkLevelsRange,
    useBenchmarkLevelsInShare,
    locationIds,
    allowedLevels,
    employees,
    currency,
    weightedJobs,
    percentiles,
    headcount,
    industry,
    fundingRounds,
    mergeAdvancedLevels,
  } = options;

  const locations = await getEmployeeLocationsForPredictiveBenchmark(ctx, { locationIds });

  if (!ctx.featureFlags.CAN_ACCESS_LEVEL_FRAMEWORKS && !useBenchmarkLevelsInShare) {
    const compensationStats = await map(levels, async (level) => {
      if (!allowedLevels.includes(level) || !weightedJobs) {
        return { level, stats: undefined };
      }

      const employeesWithLevel = employees.filter(
        hasLevel([level], employees, { mergeAdvancedLevels: !!mergeAdvancedLevels })
      );

      const stats = await computeMarketCompensationStats(ctx, employeesWithLevel, {
        level,
        locations,
        currency,
        weightedJobs,
        percentiles,
        headcount,
        industry,
        fundingRounds: fundingRounds,
      });

      return { level, stats };
    });

    return chain(compensationStats)
      .keyBy(({ level }) => level)
      .mapValues(({ stats }) => stats)
      .value();
  }

  const compensationStats = await map(benchmarkLevelsRange.benchmarkLevels, async (benchmarkLevel) => {
    if (!weightedJobs) {
      return { level: benchmarkLevel.name, stats: undefined };
    }

    const employeesWithLevel = employees.filter((employee) =>
      filterEmployeesInRange(employee, { min: benchmarkLevel.min, max: benchmarkLevel.max })
    );

    const stats = await computeMarketCompensationStats(ctx, employeesWithLevel, {
      locations,
      currency,
      weightedJobs,
      percentiles,
      headcount,
      industry,
      fundingRounds: fundingRounds,
    });

    return { benchmarkLevel, stats };
  });

  return chain(compensationStats)
    .keyBy(({ benchmarkLevel }) => benchmarkLevel?.id ?? -1) // <-- hum... can be removed once it's not 'undefined' anymore
    .mapValues(({ stats }) => stats)
    .value();
};
